import { useIsFeatureFlagEnabled } from "@circle-react/hooks/UseCommunitySetting";
import type { CommunityMember } from "@circle-react/types";
import type { SearchableSelectProps } from "@circleco/compass/components/Select";

import { CommunityMemberSelectContainsVariant } from "./CommunityMemberSelectContainsVariant";
import { CommunityMemberSelectCRMVariant } from "./CommunityMemberSelectCRMVariant";
import { CommunityMemberSelectLegacyVariant } from "./CommunityMemberSelectLegacyVariant";
import type { SelectedCommunityMember } from "./useCommunityMemberSelectOptions";

interface BaseProps {
  addable?: boolean;
  "data-testid"?: string;
  excludeAdmins?: boolean;
  filters?: Record<string, unknown>;
  getCommunityMemberText?: (member: CommunityMember) => string;
  includeInactive?: boolean;
  onSelectMember?: (member: SelectedCommunityMember) => void;
  /**
   * "contains" matches any substring of a member's name or email. "default"
   * matches names by word prefix and emails only in full. Ignored once
   * crm_clickhouse_contacts_search_pickers_enabled is on, since the CRM search
   * always matches substrings.
   */
  searchMode?: "default" | "contains";
  valueField?: "id" | "user_id";
}

interface SingleSelectProps {
  multiple?: false;
  onChange?: (value: number | string | null, displayText?: string) => void;
  value?: number | string | null;
}

interface MultiSelectComponentProps {
  multiple: true;
  onChange?: (values: (number | string)[]) => void;
  value?: (number | string)[];
}

type OmittedCompassProps =
  | "options"
  | "value"
  | "onValueChange"
  | "i18n"
  | "defaultValue"
  | "searchable"
  | "creatable"
  | "onCreateOption"
  | "creatableLabel"
  | "isLoading"
  | "loadingMessage"
  | "onInputValueChange"
  | "onOpenChangeComplete"
  | "filter"
  | "multiple";

export type CommunityMemberSelectCompassComponentProps = BaseProps &
  Omit<SearchableSelectProps<number | string>, OmittedCompassProps> &
  (SingleSelectProps | MultiSelectComponentProps);

export type CommunityMemberSelectVariantProps = Omit<
  CommunityMemberSelectCompassComponentProps,
  "searchMode"
>;

export const CommunityMemberSelectCompassComponent = (
  props: CommunityMemberSelectCompassComponentProps,
) => {
  const { searchMode, ...variantProps } = props;
  const isCrmContactsSearchEnabled = useIsFeatureFlagEnabled(
    "crm_clickhouse_contacts_search_pickers_enabled",
  );

  if (isCrmContactsSearchEnabled) {
    return <CommunityMemberSelectCRMVariant {...variantProps} />;
  }

  if (searchMode === "contains") {
    return <CommunityMemberSelectContainsVariant {...variantProps} />;
  }

  return <CommunityMemberSelectLegacyVariant {...variantProps} />;
};

CommunityMemberSelectCompassComponent.displayName =
  "CommunityMemberSelectCompassComponent";
